import React, { useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react'
import { api } from '../api'

export default function Login({ onLogin }) {
  const nav = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const isEmailValid = useMemo(() => {
    if (!email) return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  }, [email])

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      const data = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      })
      localStorage.setItem('accessToken', data.accessToken)
      onLogin?.()
      nav('/dashboard')
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .auth-root {
          min-height: 100vh;
          display: flex;
          font-family: 'DM Sans', sans-serif;
          background: #F8FAFF;
        }

        /* ── LEFT PANEL ── */
        .auth-left {
          width: 460px;
          flex-shrink: 0;
          background: linear-gradient(160deg, #1E3A8A 0%, #2563EB 55%, #3B82F6 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 3rem;
        }
        @media (max-width: 900px) { .auth-left { display: none; } }

        /* grid overlay */
        .auth-left::before {
          content: '';
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 36px 36px;
        }
        /* radial glow */
        .auth-left::after {
          content: '';
          position: absolute;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          bottom: -100px; right: -100px;
          pointer-events: none;
        }

        .auth-left-content { position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; }

        .auth-logo {
          font-family: 'Sora', sans-serif;
          font-size: 1.4rem;
          font-weight: 800;
          color: #fff;
          letter-spacing: -0.5px;
          display: flex; align-items: center; gap: 0.5rem;
          text-decoration: none;
        }
        .auth-logo-icon {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1rem;
        }

        .auth-left-headline {
          margin-top: auto;
          padding-bottom: 1rem;
        }
        .auth-left-tag {
          display: inline-block;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.9);
          font-size: 0.72rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.08em;
          padding: 0.3rem 0.8rem; border-radius: 100px;
          margin-bottom: 1.25rem;
        }
        .auth-left-headline h2 {
          font-family: 'Sora', sans-serif;
          font-size: 2rem;
          font-weight: 800;
          color: #fff;
          line-height: 1.2;
          letter-spacing: -0.03em;
          margin-bottom: 1rem;
        }
        .auth-left-headline p {
          color: rgba(255,255,255,0.7);
          font-size: 0.92rem;
          line-height: 1.65;
          max-width: 320px;
        }

        .auth-stats {
          display: flex; gap: 1.5rem;
          margin-top: 2.5rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.15);
        }
        .auth-stat-num {
          font-family: 'Sora', sans-serif;
          font-size: 1.5rem; font-weight: 800; color: #fff; line-height: 1;
        }
        .auth-stat-num span { color: #93C5FD; }
        .auth-stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.6); margin-top: 0.2rem; }

        /* testimonial card */
        .auth-testimonial {
          margin-top: 2rem;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 14px;
          padding: 1.25rem;
        }
        .auth-testimonial-text {
          font-size: 0.88rem; color: rgba(255,255,255,0.85);
          line-height: 1.6; font-style: italic;
          margin-bottom: 1rem;
        }
        .auth-testimonial-author { display: flex; align-items: center; gap: 0.65rem; }
        .auth-testimonial-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: rgba(255,255,255,0.25);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; font-weight: 700; color: #fff;
        }
        .auth-testimonial-name { font-size: 0.82rem; font-weight: 700; color: #fff; }
        .auth-testimonial-role { font-size: 0.72rem; color: rgba(255,255,255,0.55); }

        /* ── RIGHT PANEL ── */
        .auth-right {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem 2rem;
          overflow-y: auto;
        }

        .auth-form-wrap {
          width: 100%;
          max-width: 420px;
        }

        .auth-form-header { margin-bottom: 2rem; }
        .auth-form-title {
          font-family: 'Sora', sans-serif;
          font-size: 1.75rem; font-weight: 800;
          color: #0F172A; letter-spacing: -0.03em;
          margin-bottom: 0.4rem;
        }
        .auth-form-sub { color: #64748B; font-size: 0.92rem; }

        /* Mobile logo */
        .auth-mobile-logo {
          display: none;
          font-family: 'Sora', sans-serif;
          font-size: 1.2rem; font-weight: 800;
          color: #2563EB;
          margin-bottom: 2rem;
          align-items: center; gap: 0.5rem;
        }
        @media (max-width: 900px) { .auth-mobile-logo { display: flex; } }

        /* Form fields */
        .field-group { margin-bottom: 1.25rem; }
        .field-label {
          display: block;
          font-size: 0.83rem; font-weight: 600;
          color: #374151; margin-bottom: 0.45rem;
        }
        .field-wrap { position: relative; }
        .field-icon {
          position: absolute; left: 0.9rem; top: 50%; transform: translateY(-50%);
          color: #94A3B8; pointer-events: none;
          display: flex; align-items: center;
        }
        .field-input {
          width: 100%;
          padding: 0.8rem 0.9rem 0.8rem 2.6rem;
          border: 1.5px solid #E2E8F0;
          border-radius: 10px;
          font-size: 0.92rem;
          font-family: 'DM Sans', sans-serif;
          color: #0F172A;
          background: #fff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .field-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
        }
        .field-input.error {
          border-color: #F87171;
          box-shadow: 0 0 0 3px rgba(248,113,113,0.12);
        }
        .field-input-right {
          position: absolute; right: 0.9rem; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #94A3B8; display: flex; align-items: center;
          padding: 0;
        }
        .field-input-right:hover { color: #475569; }
        .field-error-text { font-size: 0.75rem; color: #EF4444; margin-top: 0.35rem; }

        .field-row-between {
          display: flex; align-items: center; justify-content: space-between;
          margin-top: 0.65rem;
        }
        .field-remember {
          display: flex; align-items: center; gap: 0.45rem;
          font-size: 0.82rem; color: #64748B; cursor: pointer;
        }
        .field-remember input { accent-color: #2563EB; }
        .field-forgot {
          font-size: 0.82rem; font-weight: 600;
          color: #2563EB; text-decoration: none;
          background: none; border: none; cursor: pointer; padding: 0;
        }
        .field-forgot:hover { text-decoration: underline; }

        /* Error banner */
        .error-banner {
          background: #FEF2F2;
          border: 1.5px solid #FECACA;
          border-radius: 10px;
          padding: 0.8rem 1rem;
          font-size: 0.85rem; color: #DC2626;
          margin-bottom: 1.25rem;
          display: flex; align-items: center; gap: 0.5rem;
        }

        /* Submit button */
        .submit-btn {
          width: 100%;
          padding: 0.9rem;
          background: #2563EB;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-family: 'Sora', sans-serif;
          font-size: 0.95rem; font-weight: 700;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 0.5rem;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(37,99,235,0.3);
        }
        .submit-btn:hover:not(:disabled) {
          background: #1D4ED8;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(37,99,235,0.4);
        }
        .submit-btn:disabled {
          background: #E2E8F0; color: #94A3B8;
          cursor: not-allowed; box-shadow: none; transform: none;
        }

        .auth-switch {
          text-align: center;
          margin-top: 1.5rem;
          font-size: 0.88rem; color: #64748B;
        }
        .auth-switch a {
          color: #2563EB; font-weight: 700; text-decoration: none;
        }
        .auth-switch a:hover { text-decoration: underline; }

        .auth-footer {
          text-align: center;
          margin-top: 2rem;
          font-size: 0.75rem; color: #CBD5E1;
        }

        .divider {
          display: flex; align-items: center; gap: 0.75rem;
          margin: 1.5rem 0;
          color: #CBD5E1; font-size: 0.78rem;
        }
        .divider::before, .divider::after {
          content: ''; flex: 1; height: 1px; background: #E2E8F0;
        }
      `}</style>

      <div className="auth-root">
        {/* LEFT PANEL */}
        <div className="auth-left">
          <div className="auth-left-content">
            <Link to="/" className="auth-logo">
              <span className="auth-logo-icon">⚡</span>
              Prorately
            </Link>

            <div className="auth-left-headline">
              <span className="auth-left-tag">For Insurance Agents</span>
              <h2>Quote in seconds.<br />Close more deals.</h2>
              <p>The AI co-pilot that autofills your carrier portals and keeps your client book organized — so you can focus on selling.</p>

              <div className="auth-stats">
                <div>
                  <div className="auth-stat-num">47<span>min</span></div>
                  <div className="auth-stat-label">saved per quote</div>
                </div>
                <div>
                  <div className="auth-stat-num">40<span>+</span></div>
                  <div className="auth-stat-label">carrier portals</div>
                </div>
                <div>
                  <div className="auth-stat-num">12k<span>+</span></div>
                  <div className="auth-stat-label">active agents</div>
                </div>
              </div>

              <div className="auth-testimonial">
                <p className="auth-testimonial-text">"I've written 30% more policies this quarter. The autofill alone pays for itself."</p>
                <div className="auth-testimonial-author">
                  <div className="auth-testimonial-avatar">SM</div>
                  <div>
                    <div className="auth-testimonial-name">Sarah Mitchell</div>
                    <div className="auth-testimonial-role">Independent Agent, Texas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="auth-right">
          <div className="auth-form-wrap">
            <div className="auth-mobile-logo">
              <span style={{ background: '#EFF6FF', borderRadius: 8, padding: '0.3rem 0.5rem', fontSize: '1rem' }}>⚡</span>
              Prorately
            </div>

            <div className="auth-form-header">
              <h1 className="auth-form-title">Welcome back</h1>
              <p className="auth-form-sub">Sign in to your Prorately account</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="field-group">
                <label className="field-label">Email address</label>
                <div className="field-wrap">
                  <span className="field-icon"><Mail size={16} /></span>
                  <input
                    className={`field-input${!isEmailValid ? ' error' : ''}`}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="you@agency.com"
                    required
                  />
                </div>
                {!isEmailValid && <p className="field-error-text">Please enter a valid email address.</p>}
              </div>

              {/* Password */}
              <div className="field-group">
                <label className="field-label">Password</label>
                <div className="field-wrap">
                  <span className="field-icon"><Lock size={16} /></span>
                  <input
                    className="field-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                    style={{ paddingRight: '2.6rem' }}
                  />
                  <button type="button" className="field-input-right" onClick={() => setShowPassword(v => !v)} aria-label="Toggle password">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="field-row-between">
                  <label className="field-remember">
                    <input type="checkbox" />
                    Remember me
                  </label>
                  <button type="button" className="field-forgot">Forgot password?</button>
                </div>
              </div>

              {error && (
                <div className="error-banner">
                  <span>⚠</span> {error}
                </div>
              )}

              <button type="submit" className="submit-btn" disabled={loading || !isEmailValid || !email || !password}>
                {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {loading ? 'Signing in…' : 'Sign in to Prorately'}
              </button>
            </form>

            <p className="auth-switch">
              Don't have an account?{' '}
              <Link to="/register">Start your free trial</Link>
            </p>

            <p className="auth-footer">
              © {new Date().getFullYear()} Prorately. All rights reserved. · <a href="#" style={{ color: '#94A3B8', textDecoration: 'none' }}>Privacy</a> · <a href="#" style={{ color: '#94A3B8', textDecoration: 'none' }}>Terms</a>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  )
}